import { SystemInfoDto } from '@/api/generator';
import { parse, valid } from 'semver';

export function uniqueFileName(fileName: string, fileNames: string[]) {
  let strCopy = fileName;

  const extensionRegEx = /(?:\.([^.]+))?$/;

  const extension = extensionRegEx.exec(fileName)?.[1];
  if (extension) strCopy = fileName.substring(0, fileName.length - (extension.length + 1));

  while (fileNames.includes(extension ? `${strCopy}.${extension}` : strCopy)) {
    strCopy = incrementFilename(strCopy);
  }

  return extension ? `${strCopy}.${extension}` : strCopy;
}

/**
 * Regular expression pattern to match filename increment suffix.
 * Matches "(n)" at the end of a string where n is one or more digits.
 *
 * @example "(1)" or "(42)" at the end of a filename
 */
const FILENAME_INCREMENT_PATTERN = /\((\d+)\)$/;

/**
 * Detect "(n)" suffixed string. Return "n"
 * @param name
 * @return The integer string or null
 */
const isFilenameIncremented = (name: string) => {
  const m = FILENAME_INCREMENT_PATTERN.exec(name);
  return m?.[1];
};

/**
 * Increment unsuffixed or "(n)" suffixed string
 * @dependency isFilenameIncremented
 * @param name
 * @return The "((n|0)+1)" suffixed filename
 */
const incrementFilename = (name: string) => {
  const isInc = isFilenameIncremented(name);
  return isInc
    ? name.replace(FILENAME_INCREMENT_PATTERN, (_, n) => `(${parseInt(n) + 1})`)
    : `${name} (1)`;
};

/**
 * Find the value of a key in a comma seperated list of key=value pairs
 * @param list
 * @param key
 * @param keyValueDelimiter
 * @example
 *
 * ```ts
 *
 *   const list = 'CN=domain.example.com, OU=PTW, O=PTW-Dosimetry, L=Freiburg, S=Baden-Wuertemberg, C=XX';
 *   const key = 'CN';
 *   const commonName = findValueInCommaSeperatedListByKey(list, key);
 *   // => 'domain.example.com'
 *
 * ```
 */
export function findValueInCommaSeperatedListByKey(
  list: string | null | undefined,
  key: string,
  keyValueDelimiter: string = '=',
) {
  return list
    ?.split(',')
    .find((item) => item.trim().startsWith(`${key}${keyValueDelimiter}`))
    ?.replace(`${key}${keyValueDelimiter}`, '');
}

/**
 * Converts a plain text address string into HTML format.
 *
 * The first line of the input text is wrapped in a <strong> tag,
 * and each subsequent line is prefixed with a <br> tag to preserve line breaks in HTML.
 *
 */
export const formatPlainTextAddressToHtml = (textString: string) => {
  const lines = textString.split('\n');

  if (lines.length === 0) return '';

  const [first, ...rest] = lines;
  const restWithBreaks = rest.map((line) => (line ? `<br>${line}` : '<br>')).join('');
  return `<strong>${first}</strong>${restWithBreaks}`;
};

export function escapeForRegex(sign: string) {
  return '\\^$*+?.()|{}[]'.includes(sign) ? `\\${sign}` : sign;
}

export function ensureUniqueClasses(classes: string) {
  const cssClassesArray = classes?.split(' ');
  const uniqueCssClasses = new Set(cssClassesArray);

  return Array.from(uniqueCssClasses).join(' ');
}

/**
 * Parses the version string and returns a new version string limited
 * to Major.Minor.Patch. If the version is undefined or has an unrecognized format,
 * this function returns an empty string.
 */
export function formatVersion(version?: SystemInfoDto['version']) {
  if (!version || !valid(version)) return '';

  const parsedVersion = parse(version);
  if (!parsedVersion) return '';

  const { major, minor, patch } = parsedVersion;

  return `${major}.${minor}.${patch}`;
}
